import type { CodeScannerStandards } from "common/code-scanner-standards";
import { CodeScannerStandard } from "common/code-scanner-standards";
import { assertNever } from "common/Support";
import type { QuaggaJSReaderConfig, QuaggaJSResultObject } from "@ericblade/quagga2/type-definitions/quagga";

export type ScanPrecision = "coarse" | "normal" | "fine";
export type Tolerance = "loose" | "normal" | "tight";

export const precisionItems: readonly ScanPrecision[] = ["coarse", "normal", "fine"];
export const toleranceItems: readonly Tolerance[] = ["loose", "normal", "tight"];

export function patchSizeForPrecision(p: ScanPrecision): "small" | "medium" | "large" {
    switch (p) {
        case "coarse":
            return "large";
        case "normal":
            return "medium";
        case "fine":
            return "small";
        default:
            return assertNever(p);
    }
}

export function quantileForScanTolerance(t: Tolerance): number {
    switch (t) {
        case "loose":
            return 0.66;
        case "normal":
            return 0.75;
        case "tight":
            return 0.84;
        default:
            return assertNever(t);
    }
}

// See https://github.com/ericblade/quagga2#decoder
// Our supported standards are explicitly encoded to generally match Quagga2's
// supported readers.
//
// There are, however, some exceptions.
export function quaggaReadersFromCodeStandards(standards: CodeScannerStandards): (string | QuaggaJSReaderConfig)[] {
    const ret: (string | QuaggaJSReaderConfig)[] = [];

    function pushEanSupplements(...supplements: ("ean_2_reader" | "ean_5_reader")[]) {
        ret.push({
            format: "ean_reader",
            config: {
                supplements,
            },
        });
    }

    for (const [index, standard] of standards.entries()) {
        switch (standard) {
            // QR codes aren't handled by Quagga
            case CodeScannerStandard.QR:
                continue;
            // UPC A translates to just upc_reader
            case CodeScannerStandard.UPC_A:
                ret.push("upc_reader");
                break;
            case CodeScannerStandard.EAN_13:
                ret.push("ean_reader");
                break;
            case CodeScannerStandard.EAN_2:
                if (ret.findIndex(i => typeof i === "object" && i.config.supplements.includes("ean_2_reader")) >= 0) {
                    continue;
                }
                if (standards.includes(CodeScannerStandard.EAN_5, index)) {
                    pushEanSupplements("ean_2_reader", "ean_5_reader");
                } else {
                    pushEanSupplements("ean_2_reader");
                }
                break;
            case CodeScannerStandard.EAN_5:
                if (ret.findIndex(i => typeof i === "object" && i.config.supplements.includes("ean_5_reader")) >= 0) {
                    continue;
                }
                if (standards.includes(CodeScannerStandard.EAN_2, index)) {
                    pushEanSupplements("ean_5_reader", "ean_2_reader");
                } else {
                    pushEanSupplements("ean_5_reader");
                }
                break;
            default:
                ret.push(standard + "_reader");
                break;
        }
    }
    return ret;
}

export function mungeFormatIntoStandard(fmt: string): CodeScannerStandard {
    // Quagga's buggy like that: the reader format is _reader for some reason.
    if (fmt === "code_32_reader") return CodeScannerStandard.Code32;
    return fmt as CodeScannerStandard;
}

const maxDecodeError = 0.15;

export function decodeErrorAcceptable(codeResult: QuaggaJSResultObject["codeResult"], quantile: number): boolean {
    const errors = codeResult.decodedCodes.map(c => c.error ?? 0).sort();
    const offset = Math.min(Math.floor(quantile * errors.length), errors.length - 1);
    return errors[offset] < maxDecodeError;
}

export function scaleFactorForWidth(width: number): number {
    if (width <= 375) return 3;
    if (width <= 750) return 2;
    if (width <= 1440) return 1;
    return 0.5;
}
